import { useTranslation } from 'react-i18next';

import {
  importedAttachmentUrl,
  importedCommentsAreOpen,
  type ConsiderationOnCard,
} from '@/api/people';
import { Eyebrow } from '@/components/ui/Eyebrow';
import { cn } from '@/lib/cn';
import { type } from '@/theme/typography';

interface Props {
  consideration: ConsiderationOnCard;
  /** The card's person — the attachment route is addressed by **both** ids, and checks the pair. */
  personId: number;
}

/**
 * **What ADP sent about this run** — `DESIGN.md` §6.5, ADR 0020 decisions 3 and 4.
 *
 * ⚠️ **A filename is a link, and the link is the only address those bytes have.** Panel stores the
 * file rather than ADP's URL — a browser cannot present the client certificate ADP's resources sit
 * behind, and an erasure can only be asserted over what we hold — and
 * `GET /api/v1/people/{person_id}/attachments/{attachment_id}/` serves them, behind this very card's
 * read rule. ⚠️ **This component said *"Opening one from here is not built yet"* for the length of
 * one afternoon**, written against a route that did not exist yet and corrected in the same pass that
 * saw it land; nothing in the product ever shipped the sentence.
 *
 * An ADP application is a person *and* a requisition, which is exactly Panel's **consideration**, so
 * an imported comment and an imported attachment hang on the run rather than on the person: Panel is
 * recording what ADP said about **this candidate at this vacancy**, not becoming a document store for
 * a human. Both blocks therefore live inside the consideration block, above the interviews — the
 * context a reader has before the conversation, then the record of the conversation itself.
 *
 * ⚠️ **The two halves sit on opposite sides of ADR 0006's gate, and the asymmetry is the decision.**
 *
 *  * **A comment is behind the gate.** ADR 0006's wording is about feedback recorded in Panel's
 *    process and an ADP comment is not that — the decision was taken on its **reasoning**, which is
 *    about the reader: a hiring manager's *"strong hire, move fast"* anchors a first-time interviewer
 *    exactly as a colleague's verdict would, and the gate has never had an exemption keyed on who
 *    wrote something. A viewer who still owes feedback for this person receives a payload with **no
 *    key at all** for these, and this component renders **nothing** for them — no heading, no count,
 *    no *"nothing here"* line. A reader must not be able to tell *"there are none"* from *"you may
 *    not read them yet"*, which is the whole reason the server drops the key instead of sending `[]`.
 *  * **An attachment is not.** ADR 0006 hides **assessments**, not facts about the candidate, and a
 *    CV is the document an interviewer must read to prepare. Hiding it would make the gate a rule
 *    about preparation rather than about independence.
 *
 * ⚠️ **One discriminator, and it is the one the interview surface already uses.**
 * `importedCommentsAreOpen` decides on the **presence of a key** the blinded shape cannot carry —
 * `isReadable`'s construction, one surface over — and there is no second question asked anywhere about
 * this. A flag, a group check or a count would each be a second definition of a protection.
 *
 * ⚠️ **An anonymised candidate's rows survive and are blank, so blankness is what is filtered.**
 * `anonymize_person` deletes an attachment's **bytes** from storage and blanks its filename, and
 * blanks every imported comment's text, keeping the rows (ADR 0009 — nothing is deleted). A filename
 * is the candidate's own and **is** personal data, so an empty one renders as nothing at all rather
 * than as an empty row: §6.5's banner is what says the erasure happened, and a list of blanks beneath
 * it would read as a broken card.
 */
export function ImportedContext({ consideration, personId }: Props) {
  const { t } = useTranslation('candidates');

  /*
   * Blank filenames and blank texts are erased rows (ADR 0009), not items with nothing in them. They
   * are dropped here rather than rendered empty, and the blocks disappear with their last item — an
   * eyebrow over an empty list is a heading claiming something exists.
   */
  const attachments = consideration.imported_attachments.filter(
    (attachment) => attachment.filename !== '',
  );

  const comments = importedCommentsAreOpen(consideration)
    ? consideration.imported_comments.filter((comment) => comment.text !== '')
    : [];

  if (attachments.length === 0 && comments.length === 0) return null;

  return (
    <>
      {attachments.length > 0 && (
        <section className="mb-6" aria-label={t('card.imported.attachments.label')}>
          <Eyebrow count={attachments.length}>{t('card.imported.attachments.label')}</Eyebrow>

          <ul className="flex flex-col gap-1">
            {attachments.map((attachment) => (
              <li key={attachment.id}>
                {/*
                 * ⚠️ **A real anchor rather than a fetch, because a download is a navigation.** The
                 * route answers the **bytes** with `Content-Disposition: attachment`, and the session
                 * cookie goes with a same-origin navigation exactly as it goes with an API call —
                 * reading a PDF into this client to hand it back to the browser would buy nothing and
                 * would put a candidate's document through the app's memory. `download` asks for the
                 * file rather than a tab, and the server's disposition header says the same thing, so
                 * a browser that ignores the attribute still does the right thing.
                 *
                 * ⚠️ **Panel claims nothing about what is in it.** The link is named with the
                 * candidate's **own filename** — the product does not read these files and does not
                 * classify one, so no screen calls a document a CV.
                 */}
                <a
                  href={importedAttachmentUrl(personId, attachment.id)}
                  download={attachment.filename}
                  /*
                   * §9: the visible text is the filename, which is what a reader is looking for, and
                   * the accessible name says what pressing it **does** — a link that downloads rather
                   * than navigates is a different thing from a link, and a screen-reader user has
                   * nothing else to tell them so. §6.8's *Active candidates* link takes the same
                   * shape for the same reason.
                   */
                  aria-label={t('card.imported.attachments.download', {
                    name: attachment.filename,
                  })}
                  className={cn(
                    type.body,
                    'rounded text-sm text-accent [overflow-wrap:anywhere] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
                  )}
                >
                  {attachment.filename}
                </a>
              </li>
            ))}
          </ul>
        </section>
      )}

      {comments.length > 0 && (
        <section className="mb-6" aria-label={t('card.imported.comments.label')}>
          <Eyebrow count={comments.length}>{t('card.imported.comments.label')}</Eyebrow>

          <ul className="flex flex-col gap-2">
            {comments.map((comment) => (
              <li
                key={comment.id}
                className={cn(type.body, 'whitespace-pre-line text-sm [overflow-wrap:anywhere]')}
              >
                {/*
                 * ⚠️ **A quotation Panel transcribes, never rewrites** — and the sentence that stood
                 * here, *"an imported comment is what ADP held when it was read"*, is **retracted**
                 * (BUG-120, 2026-11-03). ADP's `textValue` is **rich text**: 135 of the first 187
                 * comments a real import wrote carried `<p>`, `<br>`, `&nbsp;`, `<strong>` or `<li>`,
                 * and this card showed a reader the tags. `apps/adp/rich_text.py` converts it at
                 * **import**, so what arrives here is Panel's own plain-text reading of ADP's bytes.
                 * The **identity** has not moved — the comment's key is still a digest of exactly what
                 * ADP sent — so Panel quotes what ADP said and owns how it reads it. The import still
                 * updates nothing (ADR 0020 decision 3, which is ADR 0004's instinct arriving through
                 * an integration): nothing on this card offers to edit a comment, and the API has no
                 * route that would.
                 *
                 * ⚠️ **`whitespace-pre-line`, because the converter's line breaks are the content.**
                 * The text carries real newlines — a paragraph boundary, and a `- ` line per `<li>` —
                 * and HTML collapses them, so a bulleted list read as *"- a - b"* on one line until
                 * BUG-120. It is the idiom §6.7's feedback comment and the addendum list already use.
                 *
                 * ⚠️ **It is rendered as text and must stay text.** The string reached Panel inside a
                 * **remote payload**: interpreting it as markup here would hand whoever can write in
                 * ADP a script tag on a page showing a candidate's history, which is why the markup is
                 * consumed at import and why nothing on this surface may reach for
                 * `dangerouslySetInnerHTML`.
                 *
                 * ADP's author and its own timestamp are **not imported at all**, so there is no
                 * byline to render and none is invented: a field with no reader is a field that is
                 * wrong before anybody notices.
                 */}
                {comment.text}
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}
